// ============================================================================
//  USE AGORA MEDIA HOOK - Voice & Video Chat with Secure Token Auth

// ============================================================================
// Handles Agora RTC connection for voice and video chat at poker tables
// Uses Firebase Cloud Function for secure token generation
//
//  FEATURES:

// - Stores Agora UID → username mapping in Firestore
// - Fetches usernames for remote participants
// - Active speaker detection with volume indicator
// - Per-user mute/volume controls
// - 🆕 VIRTUAL BACKGROUND: Blur & custom backgrounds
// - 🆕 PERFORMANCE: Quality presets, device detection, optimizations
// ============================================================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import AgoraRTC, {
  IAgoraRTCClient,
  IMicrophoneAudioTrack,
  ICameraVideoTrack,
  IAgoraRTCRemoteUser,
  UID,
} from 'agora-rtc-sdk-ng';
import VirtualBackgroundExtension, { IVirtualBackgroundProcessor } from 'agora-extension-virtual-background';
import { auth, db } from '../firebase';
import { API_BASE } from '../utils/apiBase';
import { getVoiceAccess, voiceHeartbeat, VoiceAccess } from '../services/voiceAccessService';
import { 
  doc, 
  setDoc, 
  deleteDoc, 
  collection, 
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { CapacitorAgoraRtc, Capacitor } from '../plugins/AgoraRtcPlugin';
import { isIOSVoiceVideoBlocked } from '../utils/iosUtils';

// ============================================================================
// PLATFORM DETECTION - Use native Agora SDK on iOS/Android
// ============================================================================

const IS_NATIVE = Capacitor.isNativePlatform();

// ============================================================================
// CONFIGURATION
// ============================================================================

// MUST match the Agora project whose certificate signs tokens server-side
// (functions/.env AGORA_APP_ID / AGORA_APP_CERTIFICATE). A mismatched pair
// fails every join with a token error. Env var wins so it can be rotated
// without a code change; the fallback is the current PokerApp project.
const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID || '1a46421df9b74205b5fc031939545903';

// 🆕 PERFORMANCE: Reduce logging in production
if (process.env.NODE_ENV === 'development') {
  AgoraRTC.setLogLevel(1);
} else {
  AgoraRTC.setLogLevel(4); // Errors only in production
}

// ============================================================================
// 🆕 PERFORMANCE OPTIMIZATION SETTINGS
// ============================================================================

// Detect device capabilities once on load
const getDeviceCapabilities = () => {
  const cores = navigator.hardwareConcurrency || 4;
  const memory = (navigator as any).deviceMemory || 4; // GB, Chrome only
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isLowEnd = cores <= 2 || memory <= 2 || (isMobile && cores <= 4);
  const isHighEnd = cores >= 8 && memory >= 8 && !isMobile;
  
  return {
    cores,
    memory,
    isMobile,
    isLowEnd,
    isHighEnd,
    // Recommend quality based on device
    recommendedQuality: isLowEnd ? 'low' : (isHighEnd ? 'high' : 'medium'),
    // Should we enable virtual background by default?
    supportsVirtualBackground: cores >= 4 && !isLowEnd,
  };
};

const DEVICE_CAPABILITIES = getDeviceCapabilities();

// Quality presets - resolution, framerate, bitrate
export const VIDEO_QUALITY_PRESETS = {
  low: {
    width: 320,
    height: 240,
    frameRate: 15,
    bitrateMin: 100,
    bitrateMax: 300,
    label: 'Low (saves CPU)',
  },
  medium: {
    width: 480,
    height: 360,
    frameRate: 24,
    bitrateMin: 200,
    bitrateMax: 500,
    label: 'Medium (balanced)',
  },
  high: {
    width: 640,
    height: 480,
    frameRate: 30,
    bitrateMin: 400,
    bitrateMax: 800,
    label: 'High (best quality)',
  },
} as const;

export type VideoQuality = keyof typeof VIDEO_QUALITY_PRESETS;

// When virtual background is ON, we drop quality by one tier to save CPU
const QUALITY_WITH_BACKGROUND: Record<VideoQuality, VideoQuality> = {
  low: 'low',      // Can't go lower
  medium: 'low',   // Drop to low
  high: 'medium',  // Drop to medium
};

// ============================================================================
//  iOS NATIVE PERMISSION HELPERS

// ============================================================================

/**
 * Request microphone permission on iOS native before Agora SDK access
 * On iOS WKWebView, web-based permissions don't work - need native request
 */
async function requestMicrophonePermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    return true; // Web handles permissions automatically
  }

  const platform = Capacitor.getPlatform();
  
  if (platform === 'ios') {
    try {
      const { Microphone } = await import('@mozartec/capacitor-microphone');
      const status = await Microphone.checkPermissions();
      console.log('🎙️ [Media] iOS mic permission status:', status.microphone);
      
      if (status.microphone === 'granted') return true;
      if (status.microphone === 'denied') {
        console.warn('🎙️ [Media] Mic permission denied - user must enable in Settings');
        return false;
      }
      
      const result = await Microphone.requestPermissions();
      return result.microphone === 'granted';
    } catch (err) {
      console.error('🎙️ [Media] Error requesting iOS mic permission:', err);
      // Fallback to browser API
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch {
        return false;
      }
    }
  }
  
  // Android - use standard web API
  if (platform === 'android') {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch {
      return false;
    }
  }
  
  return true;
}

/**
 * Request camera permission on iOS native
 */
async function requestCameraPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    return true;
  }

  const platform = Capacitor.getPlatform();
  
  if (platform === 'ios') {
    try {
      const { Camera } = await import('@capacitor/camera');
      const status = await Camera.checkPermissions();
      console.log('📹 [Media] iOS camera permission status:', status.camera);
      
      if (status.camera === 'granted') return true;
      if (status.camera === 'denied') {
        console.warn('📹 [Media] Camera permission denied - user must enable in Settings');
        return false;
      }
      
      const result = await Camera.requestPermissions({ permissions: ['camera'] });
      return result.camera === 'granted';
    } catch (err) {
      console.error('📹 [Media] Error requesting iOS camera permission:', err);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch {
        return false;
      }
    }
  }
  
  if (platform === 'android') {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch {
      return false;
    }
  }
  
  return true;
}

/**
 * Request both permissions for video chat
 */
export async function requestMediaPermissions(options: { audio?: boolean; video?: boolean } = { audio: true, video: true }): Promise<{ audio: boolean; video: boolean }> {
  const results = { audio: true, video: true };
  
  if (options.audio) {
    results.audio = await requestMicrophonePermission();
  }
  if (options.video) {
    results.video = await requestCameraPermission();
  }
  
  return results;
}

// ============================================================================
// VIRTUAL BACKGROUND EXTENSION SETUP
// ============================================================================

let virtualBackgroundExtension: VirtualBackgroundExtension | null = null;
let extensionRegistered = false;

const initVirtualBackgroundExtension = () => {
  // 🆕 Skip on low-end devices
  if (!DEVICE_CAPABILITIES.supportsVirtualBackground) {
    console.log('⚠️ Virtual background disabled - device not powerful enough');
    return null;
  }

  if (!virtualBackgroundExtension) {
    virtualBackgroundExtension = new VirtualBackgroundExtension();
  }
  
  if (!extensionRegistered) {
    try {
      AgoraRTC.registerExtensions([virtualBackgroundExtension]);
      extensionRegistered = true;
      console.log('✅ Virtual background extension registered');
    } catch (err) {
      console.warn('⚠️ Virtual background extension registration failed:', err);
    }
  }
  
  return virtualBackgroundExtension;
};

// Initialize on module load (skipped on low-end devices)
initVirtualBackgroundExtension();

// ============================================================================
// CONSTANTS
// ============================================================================

const SPEAKING_VOLUME_THRESHOLD = 5;

// 🆕 PERFORMANCE: Throttle volume indicator updates
const VOLUME_INDICATOR_INTERVAL = DEVICE_CAPABILITIES.isLowEnd ? 500 : 200; // ms

// Virtual background presets
export const BACKGROUND_BLUR_LEVELS = {
  OFF: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
} as const;

export const VIRTUAL_BACKGROUND_COLORS = [
  { name: 'Green Screen', color: '#00FF00' },
  { name: 'Blue', color: '#0066FF' },
  { name: 'Dark Gray', color: '#2D2D2D' },
  { name: 'Navy', color: '#1a1a3e' },
  { name: 'Poker Green', color: '#0d4f4f' },
  { name: 'Sunset Orange', color: '#FF6B35' },
] as const;

// ============================================================================
// TYPES
// ============================================================================

export interface MediaParticipant {
  uid: UID;
  odid: string;
  username: string;
  hasAudio: boolean;
  hasVideo: boolean;
  audioTrack?: any;
  videoTrack?: any;
  isSpeaking: boolean;
  volume: number;
  isMutedByMe: boolean;
  isVideoHiddenByMe: boolean;
}

interface VoiceParticipantDoc {
  odid: string;
  username: string;
  agoraUid: number;
  joinedAt: any;
}

export type BackgroundBlurLevel = typeof BACKGROUND_BLUR_LEVELS[keyof typeof BACKGROUND_BLUR_LEVELS];

export interface BackgroundSettings {
  type: 'none' | 'blur' | 'color' | 'image';
  blurLevel?: BackgroundBlurLevel;
  color?: string;
  imageUrl?: string;
}

export interface UseAgoraMediaReturn {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  
  // Audio state
  isMuted: boolean;
  isDeafened: boolean;
  
  // Video state
  isVideoOn: boolean;
  localVideoTrack: ICameraVideoTrack | null;
  
  // Participants
  participants: MediaParticipant[];
  localUid: UID | null;
  activeSpeakerUid: UID | null;
  isLocalSpeaking: boolean;
  
  // Background state
  backgroundSettings: BackgroundSettings;
  isBackgroundSupported: boolean;
  isBackgroundLoading: boolean;
  
  // 🆕 Performance state
  videoQuality: VideoQuality;
  deviceCapabilities: typeof DEVICE_CAPABILITIES;
  
  // Actions
  // Free minutes / pass / table unlock state (see voiceAccessService)
  voiceAccess: VoiceAccess | null;
  voiceAccessAt: number;
  refreshVoiceAccess: () => Promise<VoiceAccess | null>;
  joinChannel: (channelName?: string, username?: string, opts?: { subscribeOnly?: boolean }) => Promise<void>;
  leaveChannel: () => Promise<void>;
  toggleMute: () => void;
  toggleDeafen: () => void;
  toggleVideo: () => Promise<void>;
  muteRemoteUser: (uid: UID, mute: boolean) => void;
  setRemoteUserVolume: (uid: UID, volume: number) => void;
  hideRemoteUserVideo: (uid: UID, hide: boolean) => void;
  
  // Background actions
  setBackgroundBlur: (level: BackgroundBlurLevel) => Promise<void>;
  setBackgroundColor: (color: string) => Promise<void>;
  setBackgroundImage: (imageUrl: string) => Promise<void>;
  clearBackground: () => Promise<void>;
  
  // 🆕 Performance actions
  setVideoQuality: (quality: VideoQuality) => Promise<void>;
}

// ============================================================================
// TOKEN FETCHING
// ============================================================================

interface TokenResponse {
  token: string;
  uid: number;
  channelName: string;
  expireTime: number;
}

const AGORA_TOKEN_URL = `${API_BASE}/generateAgoraToken`;

async function fetchAgoraToken(channelName: string, tableId?: string): Promise<TokenResponse> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be logged in to get voice chat token');
  }

  const idToken = await user.getIdToken();

  try {
    const response = await fetch(AGORA_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        channelName,
        role: 'publisher',
        expireTime: 7200,
        tableId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // 402 = out of free minutes and no pass; the bar shows the paywall.
      if (response.status === 402) throw new Error('free_minutes_exhausted');
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    const data = await response.json();
    console.log('🔐 Agora token fetched successfully');
    return data;
  } catch (error: any) {
    console.error('❌ Failed to fetch Agora token:', error);
    throw new Error(error.message || 'Failed to get voice chat token');
  }
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export const useAgoraMedia = (tableId: string): UseAgoraMediaReturn => {
  // Client reference
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  
  // Track references
  const localAudioTrackRef = useRef<IMicrophoneAudioTrack | null>(null);
  const localVideoTrackRef = useRef<ICameraVideoTrack | null>(null);
  
  // Virtual background processor reference
  const vbProcessorRef = useRef<IVirtualBackgroundProcessor | null>(null);
  
  // Per-user control refs
  const mutedUsersRef = useRef<Set<UID>>(new Set());
  const hiddenVideoUsersRef = useRef<Set<UID>>(new Set());
  const isDeafenedRef = useRef(false);
  const localUidRef = useRef<UID | null>(null);
  
  // Firestore UID → username mapping
  const uidToUserRef = useRef<Map<number, { odid: string; username: string }>>(new Map());
  const unsubscribeFirestoreRef = useRef<(() => void) | null>(null);
  
  // 🆕 Volume indicator throttling ref
  const lastVolumeUpdateRef = useRef<number>(0);
  
  //  PRODUCTION: Guard refs for billing protection

  const isCleaningUpRef = useRef(false);
  const currentTableIdRef = useRef<string | null>(null);
  const hasJoinedRef = useRef(false);
  const joinTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  //  PRODUCTION: Constants

  const JOIN_TIMEOUT_MS = 20000; // 20 seconds for video (longer than audio-only)

  //  NATIVE: Store plugin event listener handles for cleanup

  const nativeListenersRef = useRef<any[]>([]);
  const nativeInitializedRef = useRef(false);
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceAccess, setVoiceAccess] = useState<VoiceAccess | null>(null);
  const [voiceAccessAt, setVoiceAccessAt] = useState<number>(0);
  const refreshVoiceAccess = useCallback(async (): Promise<VoiceAccess | null> => {
    if (!tableId) return null;
    try {
      const a = await getVoiceAccess(tableId);
      setVoiceAccess((prev) => {
        if (!prev || prev.remainingSeconds !== a.remainingSeconds || prev.mode !== a.mode) setVoiceAccessAt(Date.now());
        return a;
      });
      return a;
    } catch (e) {
      console.warn('[Media] voice access check failed', e);
      return null;
    }
  }, [tableId]);
  const [localUid, setLocalUid] = useState<UID | null>(null);
  
  // Audio state
  const [isMuted, setIsMuted] = useState(false);
  // Refs mirrored for the cleanup path (which must not close over stale state)
  const isMutedRef = useRef(false);
  const isVideoOnRef = useRef(false);
  const isConnectedRef = useRef(false);
  const [isDeafened, setIsDeafened] = useState(false);
  
  // Video state
  const [isVideoOn, setIsVideoOn] = useState(false);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
  useEffect(() => { isVideoOnRef.current = isVideoOn; }, [isVideoOn]);
  useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);
  const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | null>(null);
  
  // Remote participants
  const [participants, setParticipants] = useState<MediaParticipant[]>([]);
  
  // Active speaker
  const [activeSpeakerUid, setActiveSpeakerUid] = useState<UID | null>(null);
  const [isLocalSpeaking, setIsLocalSpeaking] = useState(false);
  
  // Background state
  const [backgroundSettings, setBackgroundSettings] = useState<BackgroundSettings>({ type: 'none' });
  const [isBackgroundSupported, setIsBackgroundSupported] = useState(DEVICE_CAPABILITIES.supportsVirtualBackground);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  
  // 🆕 Performance state - default to device recommendation
  const [videoQuality, setVideoQualityState] = useState<VideoQuality>(
    DEVICE_CAPABILITIES.recommendedQuality as VideoQuality
  );

  // Keep refs in sync
  useEffect(() => {
    isDeafenedRef.current = isDeafened;
  }, [isDeafened]);

  useEffect(() => {
    localUidRef.current = localUid;
  }, [localUid]);

  // ============================================================================
  // 🆕 GET EFFECTIVE VIDEO CONFIG (considers quality + background)
  // ============================================================================

  const getVideoEncoderConfig = useCallback((quality: VideoQuality, hasBackground: boolean) => {
    // If background is active, drop quality one tier
    const effectiveQuality = hasBackground ? QUALITY_WITH_BACKGROUND[quality] : quality;
    const preset = VIDEO_QUALITY_PRESETS[effectiveQuality];
    
    return {
      width: preset.width,
      height: preset.height,
      frameRate: preset.frameRate,
      bitrateMin: preset.bitrateMin,
      bitrateMax: preset.bitrateMax,
    };
  }, []);

  // ============================================================================
  // 🆕 SET VIDEO QUALITY (runtime change)
  // ============================================================================

  const setVideoQuality = useCallback(async (quality: VideoQuality) => {
    setVideoQualityState(quality);
    
    // If video is currently on, apply the new quality
    if (localVideoTrackRef.current && clientRef.current && isConnected) {
      try {
        const hasBackground = backgroundSettings.type !== 'none';
        const config = getVideoEncoderConfig(quality, hasBackground);
        
        await localVideoTrackRef.current.setEncoderConfiguration({
          width: config.width,
          height: config.height,
          frameRate: config.frameRate,
          bitrateMin: config.bitrateMin,
          bitrateMax: config.bitrateMax,
        });
        
        console.log(`📹 Video quality changed to ${quality}${hasBackground ? ' (reduced for background)' : ''}`);
      } catch (err) {
        console.warn('⚠️ Could not change video quality:', err);
      }
    }
  }, [isConnected, backgroundSettings.type, getVideoEncoderConfig]);

  // ============================================================================
  // VIRTUAL BACKGROUND PROCESSOR MANAGEMENT
  // ============================================================================

  const initProcessor = useCallback(async (videoTrack: ICameraVideoTrack): Promise<IVirtualBackgroundProcessor | null> => {
    if (!virtualBackgroundExtension) {
      console.warn('⚠️ Virtual background extension not available');
      setIsBackgroundSupported(false);
      return null;
    }

    try {
      if (vbProcessorRef.current) {
        return vbProcessorRef.current;
      }

      console.log('🎨 Creating virtual background processor...');
      const processor = virtualBackgroundExtension.createProcessor();
      
      await processor.init();
      console.log('✅ Virtual background processor initialized');
      
      videoTrack.pipe(processor).pipe(videoTrack.processorDestination);
      
      vbProcessorRef.current = processor;
      setIsBackgroundSupported(true);
      
      return processor;
    } catch (err: any) {
      console.error('❌ Failed to initialize virtual background:', err);
      setIsBackgroundSupported(false);
      return null;
    }
  }, []);

  const destroyProcessor = useCallback(() => {
    if (vbProcessorRef.current) {
      try {
        if (localVideoTrackRef.current) {
          localVideoTrackRef.current.unpipe();
        }
        vbProcessorRef.current.release();
        vbProcessorRef.current = null;
        console.log('🗑️ Virtual background processor destroyed');
      } catch (err) {
        console.warn('⚠️ Error destroying processor:', err);
      }
    }
    setBackgroundSettings({ type: 'none' });
  }, []);

  // ============================================================================
  // 🆕 APPLY QUALITY ADJUSTMENT WHEN BACKGROUND CHANGES
  // ============================================================================

  const adjustQualityForBackground = useCallback(async (hasBackground: boolean) => {
    if (!localVideoTrackRef.current || !isConnected) return;
    
    try {
      const config = getVideoEncoderConfig(videoQuality, hasBackground);
      await localVideoTrackRef.current.setEncoderConfiguration({
        width: config.width,
        height: config.height,
        frameRate: config.frameRate,
        bitrateMin: config.bitrateMin,
        bitrateMax: config.bitrateMax,
      });
      
      if (hasBackground) {
        console.log('📉 Reduced video quality for background effect');
      } else {
        console.log('📈 Restored video quality (no background)');
      }
    } catch (err) {
      console.warn('⚠️ Could not adjust video quality:', err);
    }
  }, [isConnected, videoQuality, getVideoEncoderConfig]);

  // ============================================================================
  // BACKGROUND CONTROL METHODS
  // ============================================================================

  const setBackgroundBlur = useCallback(async (level: BackgroundBlurLevel) => {
    if (!localVideoTrackRef.current) {
      console.warn('⚠️ No video track - cannot set blur');
      return;
    }

    setIsBackgroundLoading(true);

    try {
      if (level === BACKGROUND_BLUR_LEVELS.OFF) {
        await clearBackground();
        return;
      }

      let processor = vbProcessorRef.current;
      if (!processor) {
        processor = await initProcessor(localVideoTrackRef.current);
        if (!processor) {
          setIsBackgroundLoading(false);
          return;
        }
      }

      await processor.setOptions({ type: 'blur', blurDegree: level });
      processor.enable();
      
      setBackgroundSettings({ type: 'blur', blurLevel: level });
      
      // 🆕 Adjust quality for background
      await adjustQualityForBackground(true);
      
      console.log(`🌫️ Background blur set to level ${level}`);
    } catch (err) {
      console.error('❌ Failed to set background blur:', err);
      setError('Failed to apply background blur');
    } finally {
      setIsBackgroundLoading(false);
    }
  }, [initProcessor, adjustQualityForBackground]);

  const setBackgroundColor = useCallback(async (color: string) => {
    if (!localVideoTrackRef.current) {
      console.warn('⚠️ No video track - cannot set background color');
      return;
    }

    setIsBackgroundLoading(true);

    try {
      let processor = vbProcessorRef.current;
      if (!processor) {
        processor = await initProcessor(localVideoTrackRef.current);
        if (!processor) {
          setIsBackgroundLoading(false);
          return;
        }
      }

      await processor.setOptions({ type: 'color', color });
      processor.enable();
      
      setBackgroundSettings({ type: 'color', color });
      
      // 🆕 Adjust quality for background
      await adjustQualityForBackground(true);
      
      console.log(`🎨 Background color set to ${color}`);
    } catch (err) {
      console.error('❌ Failed to set background color:', err);
      setError('Failed to apply background color');
    } finally {
      setIsBackgroundLoading(false);
    }
  }, [initProcessor, adjustQualityForBackground]);

  const setBackgroundImage = useCallback(async (imageUrl: string) => {
    if (!localVideoTrackRef.current) {
      console.warn('⚠️ No video track - cannot set background image');
      return;
    }

    setIsBackgroundLoading(true);

    try {
      let processor = vbProcessorRef.current;
      if (!processor) {
        processor = await initProcessor(localVideoTrackRef.current);
        if (!processor) {
          setIsBackgroundLoading(false);
          return;
        }
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load background image'));
        img.src = imageUrl;
      });

      await processor.setOptions({ type: 'img', source: img });
      processor.enable();
      
      setBackgroundSettings({ type: 'image', imageUrl });
      
      // 🆕 Adjust quality for background
      await adjustQualityForBackground(true);
      
      console.log(`🖼️ Background image set`);
    } catch (err) {
      console.error('❌ Failed to set background image:', err);
      setError('Failed to apply background image');
    } finally {
      setIsBackgroundLoading(false);
    }
  }, [initProcessor, adjustQualityForBackground]);

  const clearBackground = useCallback(async () => {
    setIsBackgroundLoading(true);
    
    try {
      if (vbProcessorRef.current) {
        vbProcessorRef.current.disable();
        console.log('🚫 Background effect disabled');
      }
      setBackgroundSettings({ type: 'none' });
      
      // 🆕 Restore full quality
      await adjustQualityForBackground(false);
    } catch (err) {
      console.error('❌ Failed to clear background:', err);
    } finally {
      setIsBackgroundLoading(false);
    }
  }, [adjustQualityForBackground]);

  // ============================================================================
  // FIRESTORE: STORE & FETCH USERNAME MAPPINGS
  // ============================================================================
  
  const storeVoiceParticipant = async (agoraUid: number, username: string) => {
    const user = auth.currentUser;
    if (!user || !tableId) return;

    try {
      const participantRef = doc(db, 'tables', tableId, 'voiceParticipants', String(agoraUid));
      await setDoc(participantRef, {
        odid: user.uid,
        username: username || user.displayName || 'Player',
        agoraUid: agoraUid,
        joinedAt: serverTimestamp(),
      });
      console.log('📝 Stored voice participant:', agoraUid, username);
    } catch (err) {
      console.error('Error storing voice participant:', err);
    }
  };

  const removeVoiceParticipant = async (agoraUid: number) => {
    if (!tableId) return;

    try {
      const participantRef = doc(db, 'tables', tableId, 'voiceParticipants', String(agoraUid));
      await deleteDoc(participantRef);
      console.log('🗑️ Removed voice participant:', agoraUid);
    } catch (err) {
      console.error('Error removing voice participant:', err);
    }
  };

  const subscribeToVoiceParticipants = () => {
    if (!tableId) return;

    const participantsRef = collection(db, 'tables', tableId, 'voiceParticipants');
    
    const unsubscribe = onSnapshot(participantsRef, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data() as VoiceParticipantDoc;
        
        if (change.type === 'added' || change.type === 'modified') {
          uidToUserRef.current.set(data.agoraUid, {
            odid: data.odid,
            username: data.username,
          });
          
          setParticipants(prev => prev.map(p => {
            if (Number(p.uid) === data.agoraUid) {
              return { ...p, odid: data.odid, username: data.username };
            }
            return p;
          }));
        } else if (change.type === 'removed') {
          uidToUserRef.current.delete(data.agoraUid);
        }
      });
    });

    unsubscribeFirestoreRef.current = unsubscribe;
  };

  // ============================================================================
  // AGORA EVENT HANDLERS
  // ============================================================================

  const setupEventHandlers = useCallback((client: IAgoraRTCClient) => {
    // User published (audio or video)
    client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      console.log(`📡 User ${user.uid} published ${mediaType}`);
      
      try {
        await client.subscribe(user, mediaType);
        console.log(`✅ Subscribed to ${user.uid} ${mediaType}`);

        const userInfo = uidToUserRef.current.get(Number(user.uid));

        setParticipants(prev => {
          const existing = prev.find(p => p.uid === user.uid);
          
          if (existing) {
            const updated = {
              ...existing,
              hasAudio: mediaType === 'audio' ? true : existing.hasAudio,
              hasVideo: mediaType === 'video' ? true : existing.hasVideo,
              audioTrack: mediaType === 'audio' ? user.audioTrack : existing.audioTrack,
              videoTrack: mediaType === 'video' ? user.videoTrack : existing.videoTrack,
              odid: userInfo?.odid || existing.odid,
              username: userInfo?.username || existing.username,
            };

            if (mediaType === 'audio' && user.audioTrack && 
                !isDeafenedRef.current && !mutedUsersRef.current.has(user.uid)) {
              user.audioTrack.play();
            }

            return prev.map(p => p.uid === user.uid ? updated : p);
          } else {
            const newParticipant: MediaParticipant = {
              uid: user.uid,
              odid: userInfo?.odid || '',
              username: userInfo?.username || `Player ${user.uid}`,
              hasAudio: mediaType === 'audio',
              hasVideo: mediaType === 'video',
              audioTrack: mediaType === 'audio' ? user.audioTrack : undefined,
              videoTrack: mediaType === 'video' ? user.videoTrack : undefined,
              isSpeaking: false,
              volume: 0,
              isMutedByMe: false,
              isVideoHiddenByMe: false,
            };

            if (mediaType === 'audio' && user.audioTrack && !isDeafenedRef.current) {
              user.audioTrack.play();
            }

            return [...prev, newParticipant];
          }
        });
      } catch (err) {
        console.error(`Error subscribing to ${user.uid}:`, err);
      }
    });

    // User unpublished
    client.on('user-unpublished', (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      console.log(`📴 User ${user.uid} unpublished ${mediaType}`);
      
      setParticipants(prev => prev.map(p => {
        if (p.uid === user.uid) {
          return {
            ...p,
            hasAudio: mediaType === 'audio' ? false : p.hasAudio,
            hasVideo: mediaType === 'video' ? false : p.hasVideo,
            audioTrack: mediaType === 'audio' ? undefined : p.audioTrack,
            videoTrack: mediaType === 'video' ? undefined : p.videoTrack,
          };
        }
        return p;
      }));
    });

    // User left
    client.on('user-left', (user: IAgoraRTCRemoteUser) => {
      console.log(`👋 User ${user.uid} left`);
      setParticipants(prev => prev.filter(p => p.uid !== user.uid));
      mutedUsersRef.current.delete(user.uid);
      hiddenVideoUsersRef.current.delete(user.uid);
    });

    // 🆕 PERFORMANCE: Enable volume indicator with optimized interval
    client.enableAudioVolumeIndicator();
    
    client.on('volume-indicator', (volumes) => {
      // 🆕 PERFORMANCE: Throttle updates on low-end devices
      const now = Date.now();
      if (now - lastVolumeUpdateRef.current < VOLUME_INDICATOR_INTERVAL) {
        return; // Skip this update
      }
      lastVolumeUpdateRef.current = now;
      
      let maxVolume = 0;
      let speakerUid: UID | null = null;

      volumes.forEach(({ uid, level }) => {
        if (uid === localUidRef.current) {
          setIsLocalSpeaking(level > SPEAKING_VOLUME_THRESHOLD);
        }

        if (level > maxVolume && level > SPEAKING_VOLUME_THRESHOLD) {
          maxVolume = level;
          speakerUid = uid;
        }

        setParticipants(prev => prev.map(p => {
          if (p.uid === uid) {
            return {
              ...p,
              isSpeaking: level > SPEAKING_VOLUME_THRESHOLD,
              volume: level,
            };
          }
          return p;
        }));
      });

      setActiveSpeakerUid(speakerUid);
    });

  }, []);

  // ============================================================================
  //  PRODUCTION: BULLETPROOF CLEANUP FUNCTION

  // ============================================================================
  
  const cleanupConnection = useCallback(async (reason: string = 'unknown') => {
    // Guard against concurrent cleanup calls
    if (isCleaningUpRef.current) {
      console.log('🎥 [Media] Cleanup already in progress, skipping');
      return;
    }

    isCleaningUpRef.current = true;
    const cleanupTableId = currentTableIdRef.current;

    console.log(`🎥 [Media] Cleaning up connection (reason: ${reason}, tableId: ${cleanupTableId})`);

    // 🎙 Mic follows the seat: a table move (SPA table change or unmount
    // right before a follow-the-seat hard load) leaves a stamp so the next
    // tournament table can rejoin without a tap. A deliberate leave or an
    // exhausted allowance leaves nothing behind.
    if ((reason === 'tableId_change' || reason === 'unmount' || reason === 'table_change') && isConnectedRef.current && cleanupTableId) {
      try {
        sessionStorage.setItem('pss:mediaCarry', JSON.stringify({ connected: true, fromTableId: cleanupTableId, muted: isMutedRef.current, videoOn: isVideoOnRef.current, at: Date.now() }));
      } catch { /* noop */ }
    } else if (reason === 'user_leave' || reason === 'free_minutes_exhausted') {
      try { sessionStorage.removeItem('pss:mediaCarry'); } catch { /* noop */ }
    }

    try {
      // Clear join timeout if pending
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }

      // Remove voice participant from Firestore
      if (localUidRef.current) {
        try {
          await removeVoiceParticipant(Number(localUidRef.current));
        } catch (e) {
          console.warn('🎥 [Media] Error removing voice participant:', e);
        }
      }

      // Unsubscribe from Firestore
      if (unsubscribeFirestoreRef.current) {
        unsubscribeFirestoreRef.current();
        unsubscribeFirestoreRef.current = null;
      }

      //  NATIVE CLEANUP PATH

      if (IS_NATIVE) {
        // Remove native event listeners
        if (nativeListenersRef.current.length > 0) {
          for (const listener of nativeListenersRef.current) {
            try { await listener.remove(); } catch {}
          }
          nativeListenersRef.current = [];
        }

        // Leave and destroy native SDK
        try { await CapacitorAgoraRtc.leaveChannel(); } catch (e) {
          console.warn('🎥 [Native] Error leaving channel:', e);
        }
        try { await CapacitorAgoraRtc.destroy(); } catch (e) {
          console.warn('🎥 [Native] Error destroying SDK:', e);
        }
        nativeInitializedRef.current = false;

        // Reset all state
        setIsConnected(false);
        setIsConnecting(false);
        setParticipants([]);
        setIsMuted(false);
        setIsDeafened(false);
        setIsVideoOn(false);
        setLocalUid(null);
        localUidRef.current = null;
        setActiveSpeakerUid(null);
        setIsLocalSpeaking(false);
        setError(null);
        setBackgroundSettings({ type: 'none' });
        
        mutedUsersRef.current.clear();
        hiddenVideoUsersRef.current.clear();
        uidToUserRef.current.clear();
        hasJoinedRef.current = false;

        console.log(`🎥 [Media] Native cleanup complete (reason: ${reason})`);
        return;
      }

      // WEB CLEANUP PATH
      // Destroy virtual background processor
      destroyProcessor();

      // Stop and close local audio track
      if (localAudioTrackRef.current) {
        try {
          localAudioTrackRef.current.stop();
          localAudioTrackRef.current.close();
        } catch (e) {
          console.warn('🎥 [Media] Error stopping audio track:', e);
        }
        localAudioTrackRef.current = null;
      }
      
      // Stop and close local video track
      if (localVideoTrackRef.current) {
        try {
          localVideoTrackRef.current.stop();
          localVideoTrackRef.current.close();
        } catch (e) {
          console.warn('🎥 [Media] Error stopping video track:', e);
        }
        localVideoTrackRef.current = null;
        setLocalVideoTrack(null);
      }

      // Leave Agora channel
      if (clientRef.current) {
        try {
          clientRef.current.removeAllListeners();
          await clientRef.current.leave();
          console.log('👋 [Media] Left voice/video channel');
        } catch (e) {
          console.warn('🎥 [Media] Error leaving channel:', e);
        }
        clientRef.current = null;
      }

      // Reset all state
      setIsConnected(false);
      setIsConnecting(false);
      setParticipants([]);
      setIsMuted(false);
      setIsDeafened(false);
      setIsVideoOn(false);
      setLocalUid(null);
      localUidRef.current = null;
      setActiveSpeakerUid(null);
      setIsLocalSpeaking(false);
      setError(null);
      setBackgroundSettings({ type: 'none' });
      
      mutedUsersRef.current.clear();
      hiddenVideoUsersRef.current.clear();
      uidToUserRef.current.clear();
      hasJoinedRef.current = false;

      console.log(`🎥 [Media] Cleanup complete (reason: ${reason})`);

    } catch (err) {
      console.error('🎥 [Media] Error during cleanup:', err);
    } finally {
      isCleaningUpRef.current = false;
    }
  }, [destroyProcessor]);

  // ============================================================================
  // JOIN CHANNEL
  // ============================================================================

  const joinChannel = useCallback(async (channelNameOverride?: string, username?: string, opts?: { subscribeOnly?: boolean }) => {
    // iOS NATIVE gate - shares the single tester-aware check in iosUtils so
    // the UI and the join path can never disagree again (the old blanket
    // block here also refused iOS Safari, which the banner told users to
    // open - Safari/Chrome use the web SDK path below, which carries the
    // WKWebView fixes (bypassWebAudio, speech_low_quality) built for it).
    if (isIOSVoiceVideoBlocked()) {
      console.log('🎥 [Media] Blocked on native iOS (not on tester allowlist)');
      setError('Voice & Video is coming soon to iOS');
      return;
    }

    //  PRODUCTION: Guard checks

    if (!tableId) {
      console.log('🎥 [Media] Join blocked: no tableId');
      return;
    }

    if (isConnected || isConnecting || isCleaningUpRef.current) {
      console.log('🎥 [Media] Join blocked: already connected/connecting/cleaning');
      return;
    }

    //  PRODUCTION: If switching tables, cleanup first

    if (currentTableIdRef.current && currentTableIdRef.current !== tableId) {
      console.log('🎥 [Media] Table changed, cleaning up old connection');
      await cleanupConnection('table_change');
    }

    setIsConnecting(true);
    setError(null);
    currentTableIdRef.current = tableId;

    //  PRODUCTION: Set join timeout

    joinTimeoutRef.current = setTimeout(() => {
      if (isConnecting && !isConnected) {
        console.error('🎥 [Media] Join timeout - cleaning up');
        setError('Connection timeout - please try again');
        cleanupConnection('timeout');
      }
    }, JOIN_TIMEOUT_MS);

    try {
      // Generate channel name
      const channelName = channelNameOverride || `poker_table_${tableId}`;

      // Free-minute check before anything else: out of minutes and no pass
      // means the paywall, not a connection attempt.
      const access = await refreshVoiceAccess();
      if (access && access.mode === 'exhausted') {
        if (joinTimeoutRef.current) clearTimeout(joinTimeoutRef.current);
        setIsConnecting(false);
        setError('free_minutes_exhausted');
        return;
      }

      // Fetch token from server (works on all platforms)
      console.log('🔑 Fetching Agora token...');
      const tokenData = await fetchAgoraToken(channelName, tableId);

      // Subscribe to Firestore for username mappings
      subscribeToVoiceParticipants();

      // ====================================================================
      //  NATIVE PATH: Use Capacitor plugin with native Agora SDK (iOS/Android)

      // ====================================================================
      if (IS_NATIVE) {
        console.log('📱 [Media] Using NATIVE Agora SDK');

        // Initialize native SDK
        if (!nativeInitializedRef.current) {
          await CapacitorAgoraRtc.initialize({
            appId: AGORA_APP_ID,
            enableAudio: true,
            enableVideo: true,
          });
          nativeInitializedRef.current = true;
          console.log('✅ [Media] Native Agora SDK initialized');
        }

        // Setup native event listeners
        const listeners: any[] = [];

        listeners.push(await CapacitorAgoraRtc.addListener('userJoined', (data: any) => {
          console.log('📡 [Native] User joined:', data.uid);
          const userInfo = uidToUserRef.current.get(Number(data.uid));
          setParticipants(prev => {
            if (prev.find(p => Number(p.uid) === Number(data.uid))) return prev;
            return [...prev, {
              uid: data.uid,
              odid: userInfo?.odid || '',
              username: userInfo?.username || `Player ${data.uid}`,
              hasAudio: true,
              hasVideo: false,
              audioTrack: undefined,
              videoTrack: undefined,
              isSpeaking: false,
              volume: 0,
              isMutedByMe: false,
              isVideoHiddenByMe: false,
            }];
          });
        }));

        listeners.push(await CapacitorAgoraRtc.addListener('userLeft', (data: any) => {
          console.log('👋 [Native] User left:', data.uid);
          setParticipants(prev => prev.filter(p => Number(p.uid) !== Number(data.uid)));
          mutedUsersRef.current.delete(data.uid);
          hiddenVideoUsersRef.current.delete(data.uid);
        }));

        listeners.push(await CapacitorAgoraRtc.addListener('userAudioStateChanged', (data: any) => {
          setParticipants(prev => prev.map(p =>
            Number(p.uid) === Number(data.uid) ? { ...p, hasAudio: data.enabled } : p
          ));
        }));

        listeners.push(await CapacitorAgoraRtc.addListener('userVideoStateChanged', (data: any) => {
          setParticipants(prev => prev.map(p =>
            Number(p.uid) === Number(data.uid) ? { ...p, hasVideo: data.enabled } : p
          ));
        }));

        listeners.push(await CapacitorAgoraRtc.addListener('activeSpeaker', (data: any) => {
          setActiveSpeakerUid(data.uid);
        }));

        listeners.push(await CapacitorAgoraRtc.addListener('volumeIndication', (data: any) => {
          if (data.speakers) {
            data.speakers.forEach((s: any) => {
              if (s.uid === 0 || Number(s.uid) === Number(localUidRef.current)) {
                setIsLocalSpeaking(s.volume > SPEAKING_VOLUME_THRESHOLD);
              }
              setParticipants(prev => prev.map(p =>
                Number(p.uid) === Number(s.uid) ? { ...p, isSpeaking: s.volume > SPEAKING_VOLUME_THRESHOLD, volume: s.volume } : p
              ));
            });
          }
        }));

        listeners.push(await CapacitorAgoraRtc.addListener('error', (data: any) => {
          console.error('❌ [Native] Agora error:', data);
          setError(data.message || 'Native Agora error');
        }));

        nativeListenersRef.current = listeners;

        // Join channel via native SDK
        console.log('📡 [Native] Joining channel:', channelName);
        await CapacitorAgoraRtc.joinChannel({
          token: tokenData.token,
          channelName,
          uid: tokenData.uid,
          enableAudio: true,
          enableVideo: true,
        });

        setLocalUid(tokenData.uid);
        localUidRef.current = tokenData.uid;

        // Store our username in Firestore
        await storeVoiceParticipant(tokenData.uid, username || 'Player');

        // Clear timeout
        if (joinTimeoutRef.current) {
          clearTimeout(joinTimeoutRef.current);
          joinTimeoutRef.current = null;
        }

        // Camera starts OFF, matching the web path: joining is a voice act,
        // and nobody's face should broadcast before they tap the camera on.
        // The camera track is published at join, so this mute is what keeps
        // frames from sending until toggleVideo unmutes it.
        try { await CapacitorAgoraRtc.setVideoEnabled({ enabled: false }); } catch {}

        setIsConnected(true);
        // 🎙 Rejoined after a table move (native): clear the stamp.
        try { if (JSON.parse(sessionStorage.getItem('pss:mediaCarry') || 'null')?.connected) sessionStorage.removeItem('pss:mediaCarry'); } catch { /* noop */ }
        setIsConnecting(false);
        setIsMuted(false);
        setIsVideoOn(false);
        hasJoinedRef.current = true;

        console.log('🎥 [Media] Successfully joined channel (NATIVE)');
        return; // Skip web SDK code below
      }

      // ====================================================================
      // WEB PATH: Use agora-rtc-sdk-ng (browser only)
      // ====================================================================
      console.log('🌐 [Media] Using WEB Agora SDK');

      // Create client
      const client = AgoraRTC.createClient({ 
        mode: 'rtc', 
        codec: 'vp8' 
      });
      clientRef.current = client;

      // Setup event handlers
      setupEventHandlers(client);

      //  PRODUCTION: Handle connection state changes

      client.on('connection-state-change', (curState, prevState, reason) => {
        console.log(`🎥 [Media] Connection state: ${prevState} -> ${curState} (${reason})`);
        
        if (curState === 'DISCONNECTED' && prevState === 'CONNECTED') {
          console.warn('🎥 [Media] Unexpected disconnect, cleaning up');
          cleanupConnection('disconnected');
        }
      });

      // Join channel
      console.log('📡 Joining channel:', channelName);
      await client.join(AGORA_APP_ID, channelName, tokenData.token, tokenData.uid);
      
      setLocalUid(tokenData.uid);
      localUidRef.current = tokenData.uid;

      // Store our username in Firestore for other users
      await storeVoiceParticipant(tokenData.uid, username || 'Player');

      // Recorder / watch-only join: receive everyone, publish nothing.
      if (!opts?.subscribeOnly) {
      //  iOS FIX: Request native permissions BEFORE Agora SDK access

      console.log('🔐 [Media] Requesting media permissions...');
      const permissions = await requestMediaPermissions({ audio: true, video: true });
      
      if (!permissions.audio) {
        throw new Error('Microphone permission denied. Please enable microphone access in your device Settings.');
      }

      // 🆕 PERFORMANCE: Create audio track with optimized settings
      //  iOS WKWebView FIX: bypassWebAudio is CRITICAL for iOS compatibility

      const isIOSPlatform = Capacitor.getPlatform() === 'ios';
      console.log('🎤 Creating audio track...');
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        //  CRITICAL iOS FIX: Bypass WebAudio processing for WKWebView

        bypassWebAudio: isIOSPlatform,
        // Audio optimization
        AEC: true,  // Acoustic Echo Cancellation
        AGC: true,  // Automatic Gain Control
        ANS: true,  // Automatic Noise Suppression
        // Use lower quality on iOS for stability
        encoderConfig: isIOSPlatform ? 'speech_low_quality' : (DEVICE_CAPABILITIES.isLowEnd ? 'speech_low_quality' : 'speech_standard'),
      });
      localAudioTrackRef.current = audioTrack;
      await client.publish([audioTrack]);
      console.log('✅ Audio track published');

      // Try to create video track with quality preset
      try {
        console.log('📷 Creating video track...');
        const config = getVideoEncoderConfig(videoQuality, false);
        
        const videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: {
            width: config.width,
            height: config.height,
            frameRate: config.frameRate,
            bitrateMin: config.bitrateMin,
            bitrateMax: config.bitrateMax,
          },
          facingMode: 'user',
          // 🆕 PERFORMANCE: Optimize for smoothness
          optimizationMode: 'motion',
        });
        
        localVideoTrackRef.current = videoTrack;
        setLocalVideoTrack(videoTrack);
        await client.publish([videoTrack]);
        setIsVideoOn(true);
        console.log(`✅ Video track published (${videoQuality} quality)`);
        
        // Initialize virtual background processor (only if supported)
        if (DEVICE_CAPABILITIES.supportsVirtualBackground) {
          await initProcessor(videoTrack);
        }
        
      } catch (videoErr: any) {
        console.log('📹 No video device - video disabled');
        setIsVideoOn(false);
      }
      } else {
        setIsVideoOn(false);
        console.log('🎥 [Media] Subscribe-only join (recorder)');
      }

      //  PRODUCTION: Clear timeout on successful join

      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }

      setIsConnected(true);
      setIsConnecting(false);
      setIsMuted(false);
      // 🎙 Rejoined after a table move: restore mute, clear the stamp.
      try {
        const carry = JSON.parse(sessionStorage.getItem('pss:mediaCarry') || 'null');
        if (carry && carry.connected) {
          if (carry.muted && localAudioTrackRef.current) { try { await localAudioTrackRef.current.setEnabled(false); setIsMuted(true); } catch { /* noop */ } }
          sessionStorage.removeItem('pss:mediaCarry');
        }
      } catch { /* noop */ }
      hasJoinedRef.current = true;
      
      console.log('🎥 [Media] Successfully joined channel');

    } catch (err: any) {
      console.error('🎥 [Media] Error joining channel:', err);
      setError(err.message || 'Failed to join voice chat');
      setIsConnecting(false);
      
      // Clear timeout
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }
      
      // Cleanup on failure
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }
      destroyProcessor();
      if (unsubscribeFirestoreRef.current) {
        unsubscribeFirestoreRef.current();
        unsubscribeFirestoreRef.current = null;
      }
      if (clientRef.current) {
        clientRef.current.removeAllListeners();
        await clientRef.current.leave().catch(() => {});
        clientRef.current = null;
      }
      hasJoinedRef.current = false;
    }
  }, [tableId, setupEventHandlers, isConnected, isConnecting, videoQuality, getVideoEncoderConfig, initProcessor, destroyProcessor, cleanupConnection]);

  // ============================================================================
  // LEAVE CHANNEL (public API - wraps cleanupConnection)
  // ============================================================================
  
  const leaveChannel = useCallback(async () => {
    await cleanupConnection('user_leave');
  }, [cleanupConnection]);

  // Meter free minutes: one heartbeat a minute while connected in 'free'
  // mode. The server charges the elapsed time and says when to cut off.
  // The loop must NOT depend on callback identities: re-creating the interval
  // on every render reset the 60s timer before it ever fired, so the server
  // was never charged and the countdown kept snapping back to 45:00.
  const cleanupRef = useRef(cleanupConnection);
  useEffect(() => { cleanupRef.current = cleanupConnection; }, [cleanupConnection]);
  const voiceMode = voiceAccess?.mode;
  useEffect(() => {
    if (!isConnected || !tableId || voiceMode !== 'free') return;
    let cancelled = false;
    const beat = async () => {
      try {
        const r = await voiceHeartbeat(tableId);
        if (cancelled) return;
        setVoiceAccess((prev) => {
          // Move the countdown anchor only when the server's number changed;
          // otherwise the local countdown keeps running from the last anchor.
          if (!prev || prev.remainingSeconds !== r.access.remainingSeconds || prev.mode !== r.access.mode) {
            setVoiceAccessAt(Date.now());
          }
          return r.access;
        });
        if (r.cutoff) {
          console.log('🎥 [Media] Free minutes used up, leaving voice');
          await cleanupRef.current('free_minutes_exhausted');
          setError('free_minutes_exhausted');
        }
      } catch (e) {
        console.warn('[Media] heartbeat failed', e);
      }
    };
    beat(); // stamps the session start on the server (charges nothing yet)
    const id = setInterval(beat, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
      // Charge the tail of the session on the way out
      voiceHeartbeat(tableId).catch(() => {});
    };
  }, [isConnected, tableId, voiceMode]);

  // ============================================================================
  // TOGGLE MUTE (local)
  // ============================================================================
  
  const toggleMute = useCallback(async () => {
    const newMuted = !isMuted;
    
    if (IS_NATIVE) {
      try {
        await CapacitorAgoraRtc.setAudioMuted({ muted: newMuted });
        setIsMuted(newMuted);
        console.log(`🎤 [Native] Microphone ${newMuted ? 'muted' : 'unmuted'}`);
      } catch (err) {
        console.error('🎤 [Native] Error toggling mute:', err);
      }
      return;
    }
    
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.setEnabled(!newMuted);
      setIsMuted(newMuted);
      console.log(`🎤 Microphone ${newMuted ? 'muted' : 'unmuted'}`);
    }
  }, [isMuted]);

  // ============================================================================
  // TOGGLE DEAFEN (all remote)
  // ============================================================================
  
  const toggleDeafen = useCallback(async () => {
    const newDeafened = !isDeafened;
    isDeafenedRef.current = newDeafened;

    if (IS_NATIVE) {
      // On native, mute all remote audio streams
      try {
        for (const p of participants) {
          await CapacitorAgoraRtc.setRemoteAudioMuted({ uid: Number(p.uid), muted: newDeafened });
        }
        setIsDeafened(newDeafened);
        console.log(`🔊 [Native] Audio ${newDeafened ? 'deafened' : 'restored'}`);
      } catch (err) {
        console.error('🔊 [Native] Error toggling deafen:', err);
      }
      return;
    }
    
    participants.forEach(p => {
      if (p.audioTrack) {
        if (newDeafened) {
          p.audioTrack.stop();
        } else if (!mutedUsersRef.current.has(p.uid)) {
          p.audioTrack.play();
        }
      }
    });
    
    setIsDeafened(newDeafened);
    console.log(`🔊 Audio ${newDeafened ? 'deafened' : 'restored'}`);
  }, [isDeafened, participants]);

  // ============================================================================
  // TOGGLE VIDEO (local)
  // ============================================================================
  
  const toggleVideo = useCallback(async () => {
    if (!isConnected) return;

    //  NATIVE PATH

    if (IS_NATIVE) {
      try {
        const newVideoState = !isVideoOn;
        await CapacitorAgoraRtc.setVideoEnabled({ enabled: newVideoState });
        setIsVideoOn(newVideoState);
        console.log(`📷 [Native] Video ${newVideoState ? 'enabled' : 'disabled'}`);
        // NOTE: no auto switchCamera here - the SDK already defaults to the
        // front camera, and switchCamera TOGGLES, so calling it on every
        // enable flipped players to the back camera every other time.
      } catch (err: any) {
        console.error('📷 [Native] Error toggling video:', err);
        setError(err.message || 'Failed to toggle video');
      }
      return;
    }

    // WEB PATH
    if (!clientRef.current) return;

    try {
      if (isVideoOn) {
        console.log('📷 Video turned off');
        
        destroyProcessor();
        
        if (localVideoTrackRef.current) {
          await clientRef.current.unpublish([localVideoTrackRef.current]);
          localVideoTrackRef.current.stop();
          localVideoTrackRef.current.close();
          localVideoTrackRef.current = null;
          setLocalVideoTrack(null);
        }
        setIsVideoOn(false);
      } else {
        //  iOS FIX: Request camera permission before creating video track

        const hasCamPermission = await requestCameraPermission();
        if (!hasCamPermission) {
          setError('Camera permission denied. Please enable camera access in your device Settings.');
          return;
        }
        
        console.log('📷 Creating camera track...');
        
        // 🆕 Use quality preset
        const hasBackground = backgroundSettings.type !== 'none';
        const config = getVideoEncoderConfig(videoQuality, hasBackground);
        
        const videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: {
            width: config.width,
            height: config.height,
            frameRate: config.frameRate,
            bitrateMin: config.bitrateMin,
            bitrateMax: config.bitrateMax,
          },
          facingMode: 'user',
          optimizationMode: 'motion',
        });
        
        localVideoTrackRef.current = videoTrack;
        setLocalVideoTrack(videoTrack);
        
        await clientRef.current.publish([videoTrack]);
        setIsVideoOn(true);
        console.log('✅ Video track published');
        
        // Initialize processor for new video track
        if (DEVICE_CAPABILITIES.supportsVirtualBackground) {
          await initProcessor(videoTrack);
        }
        
        // Re-apply background settings if any were set
        if (backgroundSettings.type === 'blur' && backgroundSettings.blurLevel) {
          await setBackgroundBlur(backgroundSettings.blurLevel);
        } else if (backgroundSettings.type === 'color' && backgroundSettings.color) {
          await setBackgroundColor(backgroundSettings.color);
        } else if (backgroundSettings.type === 'image' && backgroundSettings.imageUrl) {
          await setBackgroundImage(backgroundSettings.imageUrl);
        }
      }
    } catch (err: any) {
      console.error('Error toggling video:', err);
      setError(err.message || 'Failed to toggle video');
    }
  }, [isConnected, isVideoOn, videoQuality, backgroundSettings, getVideoEncoderConfig, initProcessor, destroyProcessor, setBackgroundBlur, setBackgroundColor, setBackgroundImage]);

  // ============================================================================
  // PER-USER MUTE
  // ============================================================================

  const muteRemoteUser = useCallback(async (uid: UID, mute: boolean) => {
    if (IS_NATIVE) {
      try {
        await CapacitorAgoraRtc.setRemoteAudioMuted({ uid: Number(uid), muted: mute });
        if (mute) {
          mutedUsersRef.current.add(uid);
        } else {
          mutedUsersRef.current.delete(uid);
        }
        setParticipants(prev => prev.map(p => 
          p.uid === uid ? { ...p, isMutedByMe: mute } : p
        ));
        console.log(`🔇 [Native] ${mute ? 'Muted' : 'Unmuted'} user:`, uid);
      } catch (err) {
        console.error('🔇 [Native] Error muting remote user:', err);
      }
      return;
    }

    const participant = participants.find(p => p.uid === uid);
    
    if (participant?.audioTrack) {
      if (mute) {
        participant.audioTrack.stop();
        mutedUsersRef.current.add(uid);
        console.log('🔇 Muted user:', uid);
      } else if (!isDeafenedRef.current) {
        participant.audioTrack.play();
        mutedUsersRef.current.delete(uid);
        console.log('🔊 Unmuted user:', uid);
      }
      
      setParticipants(prev => prev.map(p => 
        p.uid === uid ? { ...p, isMutedByMe: mute } : p
      ));
    }
  }, [participants]);

  // ============================================================================
  // PER-USER VOLUME
  // ============================================================================

  const setRemoteUserVolume = useCallback((uid: UID, volume: number) => {
    const participant = participants.find(p => p.uid === uid);
    
    if (participant?.audioTrack && !mutedUsersRef.current.has(uid) && !isDeafenedRef.current) {
      participant.audioTrack.setVolume(volume);
      console.log('🎚️ Set volume for user:', uid, volume);
    }
  }, [participants]);

  // ============================================================================
  // PER-USER VIDEO HIDE
  // ============================================================================

  const hideRemoteUserVideo = useCallback((uid: UID, hide: boolean) => {
    if (hide) {
      hiddenVideoUsersRef.current.add(uid);
      console.log('📵 Hidden video for user:', uid);
    } else {
      hiddenVideoUsersRef.current.delete(uid);
      console.log('📹 Showing video for user:', uid);
    }
    
    setParticipants(prev => prev.map(p => 
      p.uid === uid ? { ...p, isVideoHiddenByMe: hide } : p
    ));
  }, []);

  // ============================================================================
  //  CRITICAL: PRODUCTION CLEANUP EFFECTS

  // ============================================================================

  // 1. Cleanup on tableId change (table hopping). Also while still
  //    CONNECTING: a move that lands mid-join used to leave that join
  //    dangling until the 20 s timeout.
  useEffect(() => {
    if (currentTableIdRef.current && currentTableIdRef.current !== tableId && (isConnected || isConnecting)) {
      console.log('🎥 [Media] TableId changed, cleaning up');
      cleanupConnection('tableId_change');
    }
  }, [tableId, isConnected, isConnecting, cleanupConnection]);

  // 2. Cleanup on unmount - FULL CLEANUP, not just processor!
  useEffect(() => {
    return () => {
      console.log('🎥 [Media] Component unmounting, cleaning up');
      
      // Clear timeout
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
      }
      
      // Destroy virtual background processor
      if (vbProcessorRef.current) {
        try {
          vbProcessorRef.current.disable();
        } catch (e) {}
        vbProcessorRef.current = null;
      }
      
      // Remove voice participant from Firestore
      if (localUidRef.current && currentTableIdRef.current) {
        const participantRef = doc(db, 'tables', currentTableIdRef.current, 'voiceParticipants', String(localUidRef.current));
        deleteDoc(participantRef).catch(() => {});
      }
      
      // Unsubscribe from Firestore
      if (unsubscribeFirestoreRef.current) {
        unsubscribeFirestoreRef.current();
      }
      
      // Stop and close local audio track
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      
      // Stop and close local video track
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }
      
      // Leave Agora channel
      if (clientRef.current) {
        clientRef.current.removeAllListeners();
        clientRef.current.leave().catch(() => {});
        clientRef.current = null;
      }

      // 📱 NATIVE: everything above is web-only - in the iOS/Android app
      // it is all no-ops, so leaving the table left the NATIVE engine
      // running and iOS kept showing PSS using the microphone from the
      // lobby, the home screen, even the background. Tear the native
      // engine all the way down (destroy releases the OS audio session -
      // that is what turns the mic indicator off).
      if (IS_NATIVE && (hasJoinedRef.current || nativeInitializedRef.current)) {
        nativeListenersRef.current.forEach(l => { try { l.remove(); } catch {} });
        nativeListenersRef.current = [];
        CapacitorAgoraRtc.removeAllVideoViews?.().catch(() => {});
        CapacitorAgoraRtc.leaveChannel().catch(() => {});
        CapacitorAgoraRtc.destroy().catch(() => {});
        nativeInitializedRef.current = false;
        hasJoinedRef.current = false;
      }
    };
  }, []);

  // 3.  Cleanup on browser close/refresh (beforeunload)

  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('🎥 [Media] beforeunload - cleaning up');
      // 🎙 Follow-the-seat hard loads go through here: keep the carry stamp.
      if (isConnectedRef.current && currentTableIdRef.current) {
        try {
          sessionStorage.setItem('pss:mediaCarry', JSON.stringify({ connected: true, fromTableId: currentTableIdRef.current, muted: isMutedRef.current, videoOn: isVideoOnRef.current, at: Date.now() }));
        } catch { /* noop */ }
      }
      
      // Synchronous cleanup - can't use async here
      if (vbProcessorRef.current) {
        try {
          vbProcessorRef.current.disable();
        } catch (e) {}
      }
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
      }
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
      }
      if (clientRef.current) {
        clientRef.current.leave().catch(() => {});
      }

      // 📱 NATIVE: fire-and-forget engine teardown so a hard page unload
      // in the app can never strand a live microphone session
      if (IS_NATIVE && (hasJoinedRef.current || nativeInitializedRef.current)) {
        CapacitorAgoraRtc.leaveChannel().catch(() => {});
        CapacitorAgoraRtc.destroy().catch(() => {});
        nativeInitializedRef.current = false;
      }

      // Fire and forget - remove participant from Firestore via sendBeacon
      if (currentTableIdRef.current && localUidRef.current && hasJoinedRef.current) {
        const apiBase = API_BASE;
        navigator.sendBeacon?.(
          `${apiBase}/mediaParticipantLeft?tableId=${currentTableIdRef.current}&agoraUid=${localUidRef.current}`
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // 4.  Handle visibility change (tab switching on mobile)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isConnected) {
        console.log('🎥 [Media] Tab hidden - muting audio/video');
        // Mute audio when tab hidden
        if (localAudioTrackRef.current && !isMuted) {
          localAudioTrackRef.current.setEnabled(false);
        }
        // Disable video when tab hidden (saves bandwidth)
        if (localVideoTrackRef.current && isVideoOn) {
          localVideoTrackRef.current.setEnabled(false);
        }
      } else if (!document.hidden && isConnected) {
        console.log('🎥 [Media] Tab visible - restoring audio/video state');
        // Restore audio state
        if (localAudioTrackRef.current && !isMuted) {
          localAudioTrackRef.current.setEnabled(true);
        }
        // Restore video state
        if (localVideoTrackRef.current && isVideoOn) {
          localVideoTrackRef.current.setEnabled(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConnected, isMuted, isVideoOn]);

  // ============================================================================
  // RETURN
  // ============================================================================
  
  // ============================================================================
  //  NATIVE VIDEO VIEW POSITIONING

  // ============================================================================
  
  const setupNativeVideoView = useCallback(async (options: {
    uid?: number;
    element: HTMLElement;
    isLocal: boolean;
  }) => {
    if (!IS_NATIVE || !isConnected) return;
    
    const rect = options.element.getBoundingClientRect();
    
    try {
      if (options.isLocal) {
        await CapacitorAgoraRtc.setupLocalVideo({
          // viewId is REQUIRED by the native side - omitting it made every
          // local-video call reject with "viewId is required", which is why
          // native video never rendered.
          viewId: 'local',
          x: Math.round(rect.left),
          y: Math.round(rect.top),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
          mirror: true,
          borderRadius: 8,
        });
        console.log('📹 [Native] Local video view positioned:', rect);
      } else if (options.uid !== undefined) {
        await CapacitorAgoraRtc.setupRemoteVideo({
          viewId: `remote-${options.uid}`,
          uid: options.uid,
          x: Math.round(rect.left),
          y: Math.round(rect.top),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
          borderRadius: 8,
        });
        console.log('📹 [Native] Remote video view positioned for uid:', options.uid, rect);
      }
    } catch (err) {
      console.error('📹 [Native] Error setting up video view:', err);
    }
  }, [isConnected]);

  const removeNativeVideoView = useCallback(async (viewId: string) => {
    if (!IS_NATIVE) return;
    try {
      await CapacitorAgoraRtc.removeVideoView({ viewId });
    } catch (err) {
      console.error('📹 [Native] Error removing video view:', err);
    }
  }, []);

  const switchCamera = useCallback(async () => {
    if (!IS_NATIVE) return;
    try {
      await CapacitorAgoraRtc.switchCamera();
      console.log('📷 [Native] Camera switched');
    } catch (err) {
      console.error('📷 [Native] Error switching camera:', err);
    }
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    isConnected,
    isConnecting,
    error,
    voiceAccess,
    voiceAccessAt,
    refreshVoiceAccess,
    isMuted,
    isDeafened,
    isVideoOn,
    localVideoTrack,
    participants,
    localUid,
    activeSpeakerUid,
    isLocalSpeaking,
    
    // Background state
    backgroundSettings,
    isBackgroundSupported,
    isBackgroundLoading,
    
    // 🆕 Performance state
    videoQuality,
    deviceCapabilities: DEVICE_CAPABILITIES,
    
    // Actions
    joinChannel,
    leaveChannel,
    toggleMute,
    toggleDeafen,
    toggleVideo,
    muteRemoteUser,
    setRemoteUserVolume,
    hideRemoteUserVideo,
    
    // Background actions
    setBackgroundBlur,
    setBackgroundColor,
    setBackgroundImage,
    clearBackground,
    
    // 🆕 Performance actions
    setVideoQuality,

    //  NATIVE: Video view management & platform info

    isNativePlatform: IS_NATIVE,
    setupNativeVideoView,
    removeNativeVideoView,
    switchCamera,
  };
};

export default useAgoraMedia;